import { cx } from '@twind/core';
import useImportantSafetyInformation from 'app/hooks/useImportantSafetyInformation';
import { motion, Variants } from 'framer-motion';
import { PropsWithChildren } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

export enum ScreenAnimationType {
  Slide = 'slide',
  Fade = 'fade',
}

const variants: Record<ScreenAnimationType, Variants> = {
  slide: {
    initial: ({ isBack }) => ({
      x: isBack ? -375 : 375,
    }),
    animate: {
      x: 0,
      transition: {
        duration: 0.25,
      },
    },
    exit: ({ isBack }) => ({
      x: isBack ? 375 : -375,
      transition: {
        duration: 0.25,
      },
    }),
  },
  fade: {
    initial: {
      opacity: 0,
    },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.25,
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.25,
      },
    },
  },
};

export default function ScreenAnimation({ children }: PropsWithChildren) {
  const location = useLocation();
  const navType = useNavigationType();
  const importantSafetyInformation = useImportantSafetyInformation();
  const isBack = navType === 'POP' || location.state?.isBack;
  const shouldSkip = !isBack && location.state?.skipAnimation;
  const isFromLanding = location.state?.prevPath === '/tailor';
  const isToLanding = location.state?.nextPath === '/tailor';
  const isFromSearch = location.state?.prevPath === '/tailor/search';
  const isToSearch = location.state?.nextPath === '/tailor/search';

  const animation: ScreenAnimationType =
    isFromLanding && isToSearch && !isBack
      ? ScreenAnimationType.Fade
      : isFromSearch && isToLanding
        ? ScreenAnimationType.Fade
        : !isBack && location.state?.animation
          ? location.state?.animation
          : ScreenAnimationType.Slide;

  return (
    <motion.div
      custom={{ isBack }}
      initial="initial"
      animate="animate"
      exit="exit"
      variants={shouldSkip ? undefined : variants[animation]}
      className={cx(
        importantSafetyInformation?.is_top_position ? '' : 'flex-col-reverse',
        'flex flex-col h-full bg-white absolute w-app md:w-[375px]'
      )}
    >
      {children}
    </motion.div>
  );
}
